/**
 * The session seam.
 *
 * Resolves the active session (project dir → `session.json` → saved token),
 * builds the session-scoped RPC layer, and provides `SessionClient` to the
 * handler. Every session command (`pull`/`push`/`save`) runs through this, so
 * the resolution + auth wiring lives in exactly one place.
 *
 * @module
 */
import { Console, Effect, FileSystem, Option, Path } from "effect"
import { ChildProcessSpawner } from "effect/unstable/process"
import { OperationFailed, UsageError } from "../errors"
import { resolveProjectDir } from "../project"
import { readCredential, readSessionFile, resolveBaseUrl } from "./metadata"
import { SessionClient, sessionLayer } from "../rpc"

/** Extract a human message from any error without a type assertion. */
const describe = (error: unknown): string => {
  if (typeof error === "object" && error !== null && "message" in error) {
    const message = error.message
    if (typeof message === "string") return message
  }
  return String(error)
}

/**
 * Run one labelled step: on failure, print its message to stderr and convert it
 * to an exit-1 `OperationFailed` (which `runMain` maps to the exit code).
 */
export const step = <A, E, R>(
  name: string,
  effect: Effect.Effect<A, E, R>,
): Effect.Effect<A, OperationFailed, R> =>
  effect.pipe(
    Effect.tapError((error) => Console.error(`error: ${describe(error)}`)),
    Effect.mapError(() => new OperationFailed({ failed: [name] })),
  )

export interface CliSession {
  readonly projectDir: string
  readonly sessionId: string
  readonly versionId: string
  readonly workspaceId: string
  readonly baseUrl: string
}

/** Services a session command body may use (SessionClient is provided here). */
type SessionEnv =
  | SessionClient
  | FileSystem.FileSystem
  | Path.Path
  | ChildProcessSpawner.ChildProcessSpawner

/** Services left for the runtime to provide (SessionClient removed). */
type ResolvedEnv =
  | FileSystem.FileSystem
  | Path.Path
  | ChildProcessSpawner.ChildProcessSpawner

export const withSession = <A, E>(
  input: { readonly dir: string; readonly apiUrl: Option.Option<string> },
  use: (session: CliSession) => Effect.Effect<A, E, SessionEnv>,
): Effect.Effect<A, E | UsageError, ResolvedEnv> =>
  Effect.gen(function* () {
    const projectDir = yield* resolveProjectDir(input.dir)
    const meta = yield* readSessionFile(projectDir)
    if (meta === null) {
      return yield* Effect.fail(
        new UsageError({
          reason: "No session found here. Run `heysegment session start` first.",
        }),
      )
    }

    const baseUrl = resolveBaseUrl(Option.getOrUndefined(input.apiUrl), meta)
    const token = yield* readCredential(baseUrl, meta.sessionId)
    if (token === null) {
      return yield* Effect.fail(
        new UsageError({
          reason: `No saved credential for this session at ${baseUrl}. Re-run \`heysegment session start\`.`,
        }),
      )
    }

    return yield* use({
      projectDir,
      sessionId: meta.sessionId,
      versionId: meta.versionId,
      workspaceId: meta.workspaceId,
      baseUrl,
    }).pipe(Effect.provide(sessionLayer(baseUrl, { sessionId: meta.sessionId, token })))
  })
